/**
 * Prepares assistant text for markdown rendering.
 *
 * Two adjustments, both driven by what the models here actually emit. Across
 * 64 assistant messages there was not a single `#` heading, while a bold line
 * on its own — "**Key issues I can see:**" — appeared repeatedly and is
 * plainly doing a heading's job. Styling `h1`-`h6` alone would therefore have
 * changed nothing on screen.
 */

/** True on a line that opens or closes a fenced code block. */
const FENCE = /^\s*(```|~~~)/;

/**
 * A whole line that is nothing but bold text, with an optional trailing
 * colon. Deliberately not `.+` around the asterisks: "**a** and **b**" is a
 * sentence with two bold spans, not a heading, and must not be promoted.
 */
const STANDALONE_BOLD = /^\s*\*\*([^*]+?)\*\*(:?)\s*$/;

/** Lines whose markdown meaning would be changed by a trailing hard break. */
const STRUCTURAL = /^\s*(#{1,6}\s|>|[-*+]\s|\d+\.\s|\||:?-{3,}\s*$)/;

/**
 * Hides markup that has not finished arriving yet.
 *
 * A reply streams in character by character, so for most of its life the
 * text ends mid-construct: "**Cron trigge" has no closing pair yet, and
 * markdown therefore renders the asterisks literally until it does. Measured
 * on a live reply, raw asterisks were on screen for 518 of 692 frames — the
 * reader watches punctuation type out and then snap into formatting.
 *
 * Dropping the unclosed marker keeps the words flowing and shows them
 * unstyled until the pair completes, so the only change on completion is
 * weight or colour rather than characters vanishing.
 */
function hideUnclosedMarkup(text: string): string {
  let out = text;
  for (const marker of ["**", "`"]) {
    const parts = out.split(marker);
    // An even number of parts means an odd number of markers: one is unpaired.
    if (parts.length % 2 === 0) {
      const last = out.lastIndexOf(marker);
      out = out.slice(0, last) + out.slice(last + marker.length);
    }
  }
  return out;
}

/**
 * Promotes standalone bold lines to real headings, and preserves single
 * newlines as line breaks.
 *
 * The second part matters because the bubble previously used
 * `whitespace-pre-wrap`, where every newline showed. Markdown collapses a
 * single newline into a space, so without this a heading line and the
 * sentence beneath it would run together into one paragraph — losing both
 * the break the author intended and the heading itself.
 */
/**
 * Marks the newest word so it can be faded in as it appears.
 *
 * Markdown has no syntax for "wrap this in a span", and rehype-raw is not a
 * dependency here, so the word is emitted as a link to a private href that
 * MessageBubble renders as a span instead. A word containing link or code
 * punctuation is left alone rather than risk producing broken markup.
 */
export const FADE_TAIL_HREF = "#caw-tail";

function markNewestWord(text: string): string {
  const m = text.match(/^([\s\S]*?)(\S+)(\s*)$/);
  if (!m) return text;
  const [, head, word, tail] = m;
  if (/[[\]()`*_~<>|\\]/.test(word)) return text;
  return `${head}[${word}](${FADE_TAIL_HREF})${tail}`;
}

export function prepareChatMarkdown(
  text: string,
  { streaming = false }: { streaming?: boolean } = {}
): string {
  if (streaming) text = markNewestWord(hideUnclosedMarkup(text));
  const lines = text.split("\n");
  const out: string[] = [];
  let inFence = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (FENCE.test(line)) {
      inFence = !inFence;
      out.push(line);
      continue;
    }
    // Inside a fence every character is literal — never rewrite it.
    if (inFence) {
      out.push(line);
      continue;
    }

    const heading = line.match(STANDALONE_BOLD);
    if (heading) {
      out.push(`### ${heading[1].trim()}${heading[2]}`);
      continue;
    }

    // A hard break is only needed when a non-blank line follows and neither
    // side is structural markdown that already breaks on its own.
    const next = lines[i + 1];
    const needsBreak =
      line.trim() !== "" &&
      next !== undefined &&
      next.trim() !== "" &&
      !STRUCTURAL.test(line) &&
      !STRUCTURAL.test(next) &&
      !STANDALONE_BOLD.test(next);

    out.push(needsBreak ? `${line.replace(/\s+$/, "")}  ` : line);
  }

  return out.join("\n");
}
